import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getSpecification, getWorkflows, getSuppliers, getBudgetItems, getBudgetItemsByGl, updateSpecification, getSupplierNames } from '../../../models/specificationModel';
import SearchableSelect from '../../../components/common/SearchableSelect';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import CustomDatePicker from '../../../components/common/CustomDatePicker';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { API_BASE_URL } from '../../../config';
import api from '../../../services/api';
import { FaDownload, FaEye, FaTrash, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

function EditSpecification() {
  const navigate = useNavigate();
  const { specificationId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [filteredBudgetItems, setFilteredBudgetItems] = useState([]);
  const [loadingBudgetItems, setLoadingBudgetItems] = useState(false);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierNames, setSupplierNames] = useState({});
  
  // Helper function to get supplier count for a group
  const getSupplierCountForGroup = (groupName) => {
    return suppliers.filter(supplier => {
      if (!supplier.Group_Code) return false;
      const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
      return supplierGroups.includes(groupName);
    }).length;
  };
  
  const [formData, setFormData] = useState({
    specification_title: '',
    specification_workflow: '',
    specification_spec: '',
    specification_delivery: '',
    specification_emergency: false,
    specification_send_to: '0',
    send_group: '',
    specification_budget: '',
    specification_budget_month_id: '',
    specification_estimated_amount: '',
    specification_items: [''],
    specification_qty: [''],
    BP_Code: []
  });

  const [fileUpload, setFileUpload] = useState(null);

  // Fetch existing attachments
  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/api/attachments/specifications/${specificationId}/list?type=specification`);
      console.log('Attachments response:', response.data); // Debug log
      
      // Handle nested response structure: response.data.data.attachments
      const attachments = response.data?.data?.attachments || response.data?.attachments || [];
      setAttachments(attachments); 
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [specificationId]);
  
  // Fetch budget items when GL code changes
  useEffect(() => {
    const fetchBudgetItemsByGl = async () => {
      if (formData.specification_workflow) {
        setLoadingBudgetItems(true);
        try {
          const items = await getBudgetItemsByGl(formData.specification_workflow);
          setFilteredBudgetItems(items);
        } catch (error) {
          console.error('Error fetching budget items by GL:', error);
          setFilteredBudgetItems([]);
        } finally {
          setLoadingBudgetItems(false);
        }
      } else {
        setFilteredBudgetItems([]);
      }
    };
    fetchBudgetItemsByGl();
  }, [formData.specification_workflow]);
  
  // Debug supplier data mismatch when needed
  useEffect(() => {
    if (formData.BP_Code.length > 0 && suppliers.length > 0) {
      // Check if any suppliers match
      const hasMatch = formData.BP_Code.some(code => {
        const supplierCode = typeof code === 'object' ? code.BP_Code || code.value || code : code;
        return suppliers.some(s => s.BP_Code === supplierCode);
      });
      
      if (!hasMatch) {
        console.log('Debug: No supplier matches found between formData.BP_Code and suppliers list');
        console.log('Selected supplier codes:', formData.BP_Code);
        console.log('First few available supplier codes:', suppliers.slice(0, 5).map(s => s.BP_Code));
      }
    }
  }, [formData.BP_Code, suppliers]);

  const fetchData = async () => {
    try {
      setFetching(true);
      const [specificationData, workflowsData, suppliersData, budgetItemsData] = await Promise.all([
        getSpecification(specificationId),
        getWorkflows(),
        getSuppliers(),
        getBudgetItems()
      ]);

      // Extract the specification object from the API response
      const specification = specificationData.specification;
      
      if (!specification) {
        toast.error('Specification not found');
        setFetching(false);
        return;
      }



      // Helper function to safely convert date for CustomDatePicker (DD/MM/YY format)
      const convertDate = (dateValue) => {
        if (!dateValue) return '';
        
        // Clean up escaped slashes first
        const cleanValue = typeof dateValue === 'string' ? dateValue.replace(/\\\//g, '/') : dateValue;
        
        // If it's already in DD/MM/YY format, return as is
        if (typeof cleanValue === 'string' && cleanValue.match(/^\d{1,2}\/\d{1,2}\/\d{2}$/)) {
          const [day, month, year] = cleanValue.split('/');
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
        }
        
        // If it's in DD/MM/YYYY format, convert to DD/MM/YY
        if (typeof cleanValue === 'string' && cleanValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
          const [day, month, year] = cleanValue.split('/');
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.slice(-2)}`;
        }
        
        // If it's in YYYY-MM-DD format, convert to DD/MM/YY
        if (typeof cleanValue === 'string' && cleanValue.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
          const [year, month, day] = cleanValue.split('-');
          return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year.slice(-2)}`;
        }
        
        // If it's a timestamp
        if (typeof cleanValue === 'number' && cleanValue > 0) {
          try {
            const date = new Date(cleanValue * 1000);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
          } catch (error) {
            console.warn('Invalid date timestamp:', cleanValue);
            return '';
          }
        }
        
        // Try to parse as regular date and convert to DD/MM/YY
        try {
          const date = new Date(cleanValue);
          if (!isNaN(date.getTime())) {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
          }
        } catch (error) {
          console.warn('Invalid date value:', dateValue);
        }
        
        return '';
      };

      // Set form data from specification with error handling
      try {
        // Debug the date conversion
        console.log('Original specification_delivery:', specification.specification_delivery);
        const convertedDate = convertDate(specification.specification_delivery);
        console.log('Converted specification_delivery:', convertedDate);
        
        setFormData({
          specification_title: specification.specification_title || '',
          specification_workflow: specification.specification_workflow || '',
          specification_spec: specification.specification_spec || '',
          specification_delivery: convertedDate,
          specification_emergency: specification.specification_emergency == 1 || false,
          specification_send_to: String(specification.specification_send_to || '0'), // Convert to string for consistent comparison
          send_group: specification.send_group || '',
          specification_budget: specification.specification_budget || '',
          specification_items: Array.isArray(specification.specification_items) ? specification.specification_items : [''],
          specification_qty: Array.isArray(specification.specification_qty) ? specification.specification_qty : [''],
          BP_Code: Array.isArray(specification.specification_supplier) ? 
            // Normalize supplier data to just codes (same approach as CreateESRSpecification)
            specification.specification_supplier.map(item => {
              // Extract just the code, handling different formats
              if (typeof item === 'object' && item !== null) {
                return item.BP_Code || item.value || item;
              }
              // If it's a string that looks like JSON, try to parse it to get the code
              if (typeof item === 'string' && item.trim().startsWith('{')) {
                try {
                  const parsed = JSON.parse(item);
                  if (parsed && typeof parsed === 'object') {
                    return parsed.BP_Code || parsed.value || item;
                  }
                } catch (e) {
                  // If parsing fails, just return the original string
                  return item;
                }
              }
              // Otherwise just return the code as is
              return item;
            }) : []
        });
      } catch (error) {
        console.error('Error setting form data:', error);
        toast.error('Error processing specification data: ' + error.message);
        setFetching(false);
        return;
      }

      setWorkflows(workflowsData);
      setSuppliers(suppliersData);
      setBudgetItems(budgetItemsData);

      // Debug workflow data
      console.log('Workflows received:', workflowsData);
      console.log('Selected workflow from spec:', specification.specification_workflow);

      // Extract unique supplier groups
      const groups = [...new Set(suppliersData.map(supplier => supplier.Group_Code))];
      setSupplierGroups(groups);
      
      // Get supplier names for the selected suppliers
      if (Array.isArray(specification.specification_supplier) && specification.specification_supplier.length > 0) {
        try {
          const names = await getSupplierNames(specification.specification_supplier);
          console.log('Fetched supplier names:', names);
          setSupplierNames(names);
        } catch (error) {
          console.error('Error fetching supplier names:', error);
        }
      }

      // Fetch attachments for this specification
      await fetchAttachments();
    } catch (err) {
      toast.error('Failed to load specification: ' + err.message);
    } finally {
      setFetching(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (fileArray) => {
    if (!fileArray || fileArray.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      fileArray.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
      formData.append('type', 'specification');

      const response = await api.post(`/api/attachments/specifications/${specificationId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        // Refresh attachments list
        await fetchAttachments();
        toast.success('Files uploaded successfully!');
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Only show error if it's not a successful HTTP status code in disguise
      if (error.response?.status >= 400 || !error.response?.status) {
        toast.error('Failed to upload files: ' + (error.response?.data?.message || error.message));
      } else {
        // If it was actually successful, refresh attachments
        await fetchAttachments();
        toast.success('Files uploaded successfully!');
      }
    } finally {
      setUploading(false);
    }
  };

  // Handle file download
  const handleFileDownload = (filename) => {
    window.open(`${API_BASE_URL}/api/attachments/specifications/${filename}`, '_blank');
  };

  // Handle file deletion
  const handleFileDelete = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await api.delete(`/api/attachments/specifications/${filename}`);
      console.log('Delete response:', response.data); // Debug log
      
      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        // Refresh attachments list
        await fetchAttachments();
        toast.success('File deleted successfully!');
      } else {
        throw new Error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      // Only show error if it's not a successful HTTP status code in disguise
      if (error.response?.status >= 400 || !error.response?.status) {
        toast.error('Failed to delete file: ' + (error.response?.data?.message || error.message));
      } else {
        // If it was actually successful, refresh attachments
        await fetchAttachments();
        toast.success('File deleted successfully!');
      }
    }
  };  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleWysiwygChange = (value) => {
    setFormData(prev => ({
      ...prev,
      specification_spec: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.specification_items];
    const newQty = [...formData.specification_qty];
    
    if (field === 'item') {
      newItems[index] = value;
    } else if (field === 'qty') {
      newQty[index] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      specification_items: newItems,
      specification_qty: newQty
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      specification_items: [...prev.specification_items, ''],
      specification_qty: [...prev.specification_qty, '']
    }));
  };

  const removeItem = (index) => {
    const newItems = formData.specification_items.filter((_, i) => i !== index);
    const newQty = formData.specification_qty.filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      specification_items: newItems,
      specification_qty: newQty
    }));
  };

  const handleSupplierChange = (supplierCode, checked, supplierName = null) => {
    let newSelected = [...formData.BP_Code];
    
    if (checked) {
      // Store just the supplier code (same as CreateESRSpecification)
      newSelected.push(supplierCode);
      
      // Update our local supplier names cache for display purposes
      if (supplierName) {
        setSupplierNames(prev => ({
          ...prev,
          [supplierCode]: supplierName
        }));
      }
    } else {
      newSelected = newSelected.filter(code => {
        const compareCode = String(typeof code === 'object' ? code.BP_Code || code.value || code : code);
        return compareCode !== String(supplierCode);
      });
    }
    
    setFormData(prev => ({
      ...prev,
      BP_Code: newSelected
    }));
  };

  const handleOldFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileUpload(file);
    }
  };

  const validateForm = () => {
    if (!formData.specification_title.trim()) {
      toast.error('Specification title is required');
      return false;
    }
    
    if (!formData.specification_workflow) {
      toast.error('Workflow is required');
      return false;
    }
    
    if (!formData.specification_delivery) {
      toast.error('Expected delivery date is required');
      return false;
    }
    
    // Validate items (allow negative quantities)
    const hasValidItems = formData.specification_items.some((item, index) => 
      item.trim() && formData.specification_qty[index] && parseFloat(formData.specification_qty[index]) !== 0
    );
    
    if (!hasValidItems) {
      toast.error('At least one item with quantity is required');
      return false;
    }
    
    // Validate suppliers if send_to is selected
    if (formData.specification_send_to === '1' && formData.BP_Code.length === 0) {
      toast.error('Please select at least one supplier');
      return false;
    }
    
    if (formData.specification_send_to === '2' && !formData.send_group) {
      toast.error('Please select a supplier group');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare data for submission
      console.log('Submit: specification_send_to =', formData.specification_send_to, 'type:', typeof formData.specification_send_to);
      console.log('Submit: BP_Code count =', formData.BP_Code.length);
      
      const submitData = {
        ...formData,
        specification_items: formData.specification_items.filter(item => item.trim()),
        specification_qty: formData.specification_qty.filter((qty, index) => 
          formData.specification_items[index] && formData.specification_items[index].trim()
        ),
        // Send BP_Code as a raw array for selected suppliers (same as CreateESRSpecification)
        // The API will serialize it properly on the backend
        BP_Code: formData.specification_send_to === '1' ? formData.BP_Code.map(item => {
          // Extract just the supplier code, whether it's a string or object
          if (typeof item === 'object' && item !== null) {
            return item.BP_Code || item.value || item;
          }
          return item;
        }) : [],
        specification_workflow: formData.specification_workflow, // Explicitly ensure this is included
      };
      
      console.log('Submit: Final BP_Code to send =', submitData.BP_Code);
      
      const response = await updateSpecification(specificationId, submitData);
      
      if (response.status === 1) {
        toast.success('Specification updated successfully!');
        // Redirect to the correct specifications list based on emergency flag
        if (formData.specification_emergency) {
          navigate('/dashboard/specifications/esr');
        } else {
          navigate('/dashboard/specifications');
        }
      } else {
        toast.error('Failed to update specification');
      }
    } catch (err) {
      toast.error('Failed to update specification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading specification...</div>
      </div>
    );
  }



  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => {
            // Navigate back to the correct specifications list based on emergency flag
            if (formData.specification_emergency) {
              navigate('/dashboard/specifications/esr');
            } else {
              navigate('/dashboard/specifications');
            }
          }}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Specifications
        </button>
        <h1 className="text-2xl font-semibold text-gray-800">Edit Specification</h1>
        <p className="text-gray-600 mt-1">Update the specification details below</p>
      </div>



      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          
          {/* Title and Delivery Date in same row */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-8">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Specification Title *
              </label>
              <input
                type="text"
                name="specification_title"
                value={formData.specification_title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                placeholder="Enter specification title"
                required
              />
            </div>
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Expected Delivery Date *
              </label>
              <CustomDatePicker
                value={formData.specification_delivery}
                onChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  specification_delivery: value
                }))}
                placeholder="DD/MM/YY"
                required
              />
            </div>
          </div>
          
          {/* Full width GL Code */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              GL Code *
            </label>
            <SearchableSelect
              items={workflows
                .filter(wf => wf.mid > 0 || wf.second > 0 || wf.approval > 0)
                .map(wf => ({
                  value: wf.gl_code,
                  label: `${wf.gl_code} - ${wf.description}`
                }))}
              onSelectItem={(selectedItem) => {
                setFormData(prev => ({
                  ...prev,
                  specification_workflow: selectedItem ? selectedItem.value : ''
                }));
              }}
              initialSelectedItem={formData.specification_workflow 
                ? workflows
                    .filter(wf => wf.mid > 0 || wf.second > 0 || wf.approval > 0)
                    .map(wf => ({
                      value: wf.gl_code,
                      label: `${wf.gl_code} - ${wf.description}`
                    }))
                    .find(item => item.value === formData.specification_workflow)
                : null}
              placeholder="Select GL Code"
            />
          </div>

          {/* Budget Item Section - Shown when GL code has associated budget items */}
          {filteredBudgetItems.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-800 mb-3">Budget Allocation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget Item
                  </label>
                  {loadingBudgetItems ? (
                    <div className="text-sm text-gray-500">Loading budget items...</div>
                  ) : (
                    <select
                      name="specification_budget"
                      value={formData.specification_budget}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedItem = filteredBudgetItems.find(i => i.id === parseInt(selectedId));
                        setFormData(prev => ({
                          ...prev,
                          specification_budget: selectedId,
                          specification_budget_month_id: selectedItem?.budget_month_id || ''
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                    >
                      <option value="">Select a budget item</option>
                      {filteredBudgetItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} {item.remaining_budget !== undefined ? `(R${item.remaining_budget.toLocaleString()} available)` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Amount (R)
                  </label>
                  <input
                    type="number"
                    name="specification_estimated_amount"
                    value={formData.specification_estimated_amount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                    placeholder="Enter estimated amount"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              {/* Budget Info Display */}
              {formData.specification_budget && (
                <div className="mt-3 p-3 bg-white rounded border">
                  {(() => {
                    const selectedItem = filteredBudgetItems.find(i => i.id === parseInt(formData.specification_budget));
                    if (!selectedItem) return null;
                    
                    const estimatedAmount = parseFloat(formData.specification_estimated_amount) || 0;
                    const remainingAfter = selectedItem.remaining_budget - estimatedAmount;
                    const isOverBudget = remainingAfter < 0;
                    
                    return (
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Budget Period:</span>
                          <span className="font-medium">{selectedItem.budget_month} {selectedItem.budget_year}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Budget Amount:</span>
                          <span className="font-medium">R{(selectedItem.budget_amount || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Already Spent:</span>
                          <span className="font-medium text-orange-600">R{(selectedItem.total_spent || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Currently Available:</span>
                          <span className="font-medium text-green-600">R{(selectedItem.remaining_budget || 0).toLocaleString()}</span>
                        </div>
                        {estimatedAmount > 0 && (
                          <div className={`flex justify-between pt-2 border-t ${isOverBudget ? 'text-red-600' : 'text-blue-600'}`}>
                            <span>After This Spec:</span>
                            <span className="font-bold">R{remainingAfter.toLocaleString()}</span>
                          </div>
                        )}
                        {isOverBudget && (
                          <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-xs">
                            ⚠️ This specification exceeds available budget by R{Math.abs(remainingAfter).toLocaleString()}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="specification_emergency"
                checked={formData.specification_emergency}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">ESR</span>
            </label>
          </div>
        </div>

        {/* Specification Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Specification Details</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Specification Details
            </label>
            <WysiwygEditor
              value={formData.specification_spec}
              onChange={handleWysiwygChange}
              placeholder="Enter detailed specification requirements..."
            />
          </div>
        </div>

        {/* Items */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-[#0099e0] border border-transparent rounded-md hover:bg-[#0077b3]"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Add Item
            </button>
          </div>

          {formData.specification_items.map((item, index) => (
            <div key={index} className="flex items-center space-x-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleItemChange(index, 'item', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                  placeholder="Item description"
                />
              </div>
              <div className="w-32">
                <input
                  type="number"
                  value={formData.specification_qty[index]}
                  onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0099e0] focus:border-[#0099e0]"
                  placeholder="Qty"
                  step="0.01"
                />
              </div>
              {formData.specification_items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-2 text-red-600 hover:text-red-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Supplier Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Supplier Selection</h2>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Send to suppliers:</label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={formData.specification_send_to !== '0'}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      specification_send_to: e.target.checked ? '1' : '0'
                    }));
                  }}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {formData.specification_send_to !== '0' ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>
          
          {/* Always show supplier selection interface */}
          <div className="space-y-4">            
            {/* Dual Pane Supplier Selection */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Available Suppliers */}
              <div className="border border-gray-300 rounded-lg">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Available Suppliers</h4>
                  <input
                    type="text"
                    placeholder="Search suppliers..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Click on a supplier to add it to the selected list</p>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {suppliers
                    .filter(supplier => {
                      // Check if supplier is already selected (handle both string and object formats)
                      const isSelected = formData.BP_Code.some(code => {
                        const supplierCode = String(typeof code === 'object' ? code.BP_Code || code.value || code : code);
                        return supplierCode === String(supplier.BP_Code);
                      });
                      
                      // Filter by search term
                      const matchesSearch = supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase());
                      
                      return !isSelected && matchesSearch;
                    })
                    .map((supplier) => (
                      <div
                        key={supplier.BP_Code}
                        onClick={() => {
                          // Store both code and name
                          setFormData(prev => ({
                            ...prev,
                            BP_Code: [...prev.BP_Code, {
                              BP_Code: supplier.BP_Code,
                              BP_Name: supplier.BP_Name
                            }],
                            specification_send_to: '1' // Auto-enable when selecting suppliers
                          }));
                          
                          // Update our local supplier names cache
                          setSupplierNames(prev => ({
                            ...prev,
                            [supplier.BP_Code]: supplier.BP_Name
                          }));
                        }}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-green-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {supplier.BP_Name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ml-2 text-green-600 hover:text-green-800 flex-shrink-0"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  {suppliers.filter(supplier => {
                    // Check if supplier is already selected (handle both string and object formats)
                    const isSelected = formData.BP_Code.some(code => {
                      const supplierCode = String(typeof code === 'object' ? code.BP_Code || code.value || code : code);
                      return supplierCode === String(supplier.BP_Code);
                    });
                    
                    // Filter by search term
                    const matchesSearch = supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase());
                    
                    return !isSelected && matchesSearch;
                  }).length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="text-sm">No suppliers found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Suppliers */}
              <div className="border border-gray-300 rounded-lg">
                <div className="bg-green-50 px-4 py-3 border-b border-gray-300">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      Selected Suppliers ({formData.BP_Code.length})
                    </h4>
                    <div className="flex items-center gap-2">
                      {formData.BP_Code.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, BP_Code: [] }))}
                          className="text-xs text-red-600 hover:text-red-800 font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {formData.BP_Code.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-sm">No suppliers selected</p>
                      <p className="text-xs text-gray-400 mt-1">Click suppliers from the left to add them</p>
                    </div>
                  ) : (
                    <>
                      {/* Show a note if we have suppliers but no names */}
                      {formData.BP_Code.length > 0 && 
                       Object.keys(supplierNames).length === 0 &&
                       !formData.BP_Code.some(code => {
                         const supplierCode = String(typeof code === 'object' ? code.BP_Code || code.value || code : code);
                         // Check if this code has a name either in the object or in the suppliers list
                         return (typeof code === 'object' && code.BP_Name) || 
                                suppliers.some(s => String(s.BP_Code) === supplierCode);
                       }) && (
                        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-xs text-yellow-700">
                          Getting supplier names... If names don't appear, please save and reload the page.
                        </div>
                      )}
                      {formData.BP_Code.map((code) => {
                        // Extract all possible identifiers from the supplier code
                        let supplierCode, supplierName;
                        
                        if (typeof code === 'object' && code !== null) {
                          // Get the code for identification
                          supplierCode = code.BP_Code || code.value || code.id || code.code || '';
                          
                          // If the object has a name property, use it directly
                          if (code.BP_Name || code.name || code.label) {
                            supplierName = code.BP_Name || code.name || code.label;
                          }
                        } else {
                          supplierCode = code;
                        }
                        
                        // If we don't have a name yet, try multiple sources
                        if (!supplierName) {
                          // 1. Check our supplier names cache first
                          if (supplierNames[supplierCode]) {
                            supplierName = supplierNames[supplierCode];
                          } 
                          // 2. Try to find in the available suppliers list
                          else {
                            const supplier = suppliers.find(s => String(s.BP_Code) === String(supplierCode));
                            if (supplier && supplier.BP_Name) {
                              supplierName = supplier.BP_Name;
                            } 
                            // 3. As a last resort, show "Supplier" + code
                            else {
                              supplierName = `Supplier ${supplierCode}`;
                            }
                          }
                        }
                        
                        return (
                          <div 
                            key={supplierCode}
                            className="flex items-center justify-between p-2 bg-white border-b border-gray-100 last:border-b-0"
                          >
                            <span className="text-sm font-medium">{supplierName}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  BP_Code: prev.BP_Code.filter(c => {
                                    const compareCode = String(typeof c === 'object' ? c.BP_Code || c.value || c : c);
                                    return compareCode !== String(supplierCode);
                                  })
                                }));
                              }}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            {suppliers.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    // Include both code and name when selecting all
                    const allSuppliers = suppliers.map(s => ({
                      BP_Code: s.BP_Code,
                      BP_Name: s.BP_Name
                    }));
                    
                    // Also update our supplier names cache
                    const newNames = {};
                    suppliers.forEach(s => {
                      newNames[s.BP_Code] = s.BP_Name;
                    });
                    
                    setSupplierNames(prev => ({...prev, ...newNames}));
                    
                    setFormData(prev => ({ 
                      ...prev, 
                      BP_Code: allSuppliers,
                      specification_send_to: '1', // Auto-enable when selecting all
                      send_group: '' // Clear group selection
                    }));
                  }}
                  className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    BP_Code: [],
                    send_group: '' // Also clear group selection
                  }))}
                  className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                >
                  Clear All
                </button>
                <button
                  type="button"
                  onClick={() => setSupplierSearch('')}
                  className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                  Clear Search
                </button>
              </div>
            )}

            {/* Supplier Group Selection - Only show if enabled and no individual suppliers selected */}
            {formData.specification_send_to !== '0' && formData.BP_Code.length === 0 && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Or Send to Supplier Group
                </label>
                <select
                  name="send_group"
                  value={formData.send_group}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      send_group: e.target.value,
                      specification_send_to: e.target.value ? '2' : '1'
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                >
                  <option value="">Select a group</option>
                  {supplierGroups.map((group) => (
                    <option key={group} value={group}>
                      {group} ({getSupplierCountForGroup(group)} supplier{getSupplierCountForGroup(group) !== 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose either individual suppliers or a supplier group, not both.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Attachments</h2>
          
          {/* Upload Section */}
          <div className="mb-6">
            <MultiFileInput
              files={[]}
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.txt,.jpg,.png,.gif"
              maxSize={10 * 1024 * 1024}
              multiple={true}
            />
          </div>

          {/* Existing Attachments */}
          {attachments.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Current Files</h3>
              <div className="space-y-2">
                {attachments.map((attachment) => (
                  <div key={attachment.attachment_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <FaFileAlt className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700">{attachment.file_name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({Math.round(attachment.file_size / 1024)} KB)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleFileDownload(attachment.file_path)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                        title="Download"
                      >
                        <FaDownload className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleFileDelete(attachment.file_path)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              // Navigate to the correct specifications list based on emergency flag
              if (formData.specification_emergency) {
                navigate('/dashboard/specifications/esr');
              } else {
                navigate('/dashboard/specifications');
              }
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Update Specification
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditSpecification; 